import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import TiptapEditor from '../../components/Editor/TiptapEditor';
import { paraSalvar } from '../../components/Editor/tiptapBase';
import MultiSelectBusca from '../../components/MultiSelectBusca';
import YoutubePicker from '../../components/YoutubePicker';
import api from '../../api';

const TIPOS = [
  { v: 'pop', l: 'POP' },
  { v: 'manual', l: 'Manual' },
  { v: 'descricao_cargo', l: 'Descrição de cargo' },
  { v: 'informativo', l: 'Informativo' },
  { v: 'treinamento_video', l: 'Treinamento (vídeo)' },
  { v: 'cultura', l: 'Cultura' },
];
// Ata não é documento: mora no módulo de Atas, com pasta e permissão próprias.

function parseConteudo(c) {
  if (!c) return null;
  if (typeof c === 'object') return c;
  try { return JSON.parse(c); } catch (_) { return null; }
}

export default function DocumentoEditor() {
  const { id } = useParams();
  const editando = !!id;
  const navigate = useNavigate();

  const [categorias, setCategorias] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [pronto, setPronto] = useState(!editando); // só renderiza o editor quando o conteúdo carregou
  const [salvando, setSalvando] = useState(false);

  const [form, setForm] = useState({
    titulo: '', tipo: 'pop', categoria_id: '', resumo: '', youtube_id: '',
    obriga_marcacao_leitura: false, perfis: [],
  });
  const [conteudo, setConteudo] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/categorias'), api.get('/perfis')])
      .then(([c, p]) => { setCategorias(c.data.categorias); setPerfis(p.data.perfis); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!editando) return;
    api.get(`/documentos/${id}`).then(({ data }) => {
      const d = data.documento;
      setForm({
        titulo: d.titulo || '', tipo: d.tipo || 'pop', categoria_id: d.categoria_id || '',
        resumo: d.resumo || '', youtube_id: d.youtube_id || '',
        obriga_marcacao_leitura: !!d.obriga_marcacao_leitura, perfis: data.perfis || [],
      });
      setConteudo(parseConteudo(d.conteudo));
      setPronto(true);
    }).catch(() => { Swal.fire('Erro', 'Não foi possível carregar o documento.', 'error'); navigate('/documentos/manuais'); });
  }, [id, editando, navigate]);

  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const perfisOptions = perfis.map(p => ({ value: p.id, label: p.titulo }));

  async function salvar(e) {
    e.preventDefault();
    if (!form.titulo.trim()) return Swal.fire('Falta o título', 'Dê um título ao documento.', 'warning');
    if (!form.categoria_id) return Swal.fire('Falta a categoria', 'Escolha uma categoria.', 'warning');

    setSalvando(true);
    const payload = {
      titulo: form.titulo.trim(),
      tipo: form.tipo,
      categoria_id: form.categoria_id,
      resumo: form.resumo.trim() || null,
      youtube_id: form.youtube_id.trim() || null,
      obriga_marcacao_leitura: form.obriga_marcacao_leitura,
      perfis: form.perfis,
      conteudo: conteudo ? paraSalvar(conteudo) : null,
    };
    try {
      let docId = id;
      if (editando) await api.put(`/documentos/${id}`, payload);
      else { const { data } = await api.post('/documentos', payload); docId = data.id; }
      await Swal.fire({ icon: 'success', title: 'Salvo!', timer: 1300, showConfirmButton: false });
      navigate(form.tipo === 'cultura' ? '/cultura' : `/documentos/${docId}`);
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível salvar.', 'error');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Header />
      <main className="conteudo-container" style={{ maxWidth: 900 }}>
        <Link to="/documentos/manuais" className="doc-voltar"><i className="bi bi-arrow-left"></i> Manuais e POPs</Link>
        <h1 style={{ color: 'var(--verde)', fontWeight: 700, marginBottom: 16 }}>
          {editando ? 'Editar documento' : 'Novo documento'}
        </h1>

        <form className="conteudo-card doc-form" onSubmit={salvar}>
          <label className="doc-label">Título *</label>
          <input className="doc-input" value={form.titulo} onChange={e => set('titulo', e.target.value)} maxLength={200} autoFocus />

          <div className="doc-form-linha">
            <div style={{ flex: 1 }}>
              <label className="doc-label">Tipo</label>
              <select className="doc-input" value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                {TIPOS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="doc-label">Categoria *</label>
              <select className="doc-input" value={form.categoria_id} onChange={e => set('categoria_id', e.target.value)}>
                <option value="">Escolha…</option>
                {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
          </div>

          <label className="doc-label">Resumo</label>
          <input className="doc-input" value={form.resumo} onChange={e => set('resumo', e.target.value)} maxLength={500} placeholder="Uma linha que aparece na busca" />

          <label className="doc-label">Vídeo do YouTube</label>
          <YoutubePicker value={form.youtube_id} onChange={v => set('youtube_id', v)} />

          <label className="doc-label">Conteúdo</label>
          {pronto
            ? <TiptapEditor valorInicial={conteudo} onChange={setConteudo} />
            : <div className="doc-input">Carregando editor…</div>}

          <label className="doc-label" style={{ marginTop: 16 }}>Quem pode ver</label>
          <p className="doc-hint">Busque e adicione os cargos que terão acesso. Sem nenhum = visível a <strong>todos</strong>.</p>
          <MultiSelectBusca
            options={perfisOptions}
            values={form.perfis}
            onChange={(vals) => set('perfis', vals)}
            placeholder="Buscar cargo…"
            icon="bi-people"
          />
          {form.perfis.length === 0 && <div className="doc-perfis-vazio">Visível a todos os colaboradores.</div>}

          <label className="doc-check">
            <input type="checkbox" checked={form.obriga_marcacao_leitura} onChange={e => set('obriga_marcacao_leitura', e.target.checked)} />
            Exigir confirmação de leitura ("li e entendi")
          </label>

          <div className="doc-form-acoes">
            <button type="submit" className="btn-centrao" disabled={salvando}>
              {salvando ? 'Salvando…' : (editando ? 'Salvar alterações' : 'Publicar documento')}
            </button>
            <Link to="/documentos/manuais" className="btn btn-outline-secondary">Cancelar</Link>
          </div>
        </form>
      </main>
    </>
  );
}
